import React, { useState, useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';
import { styled } from '@mui/material/styles';
import InfoIcon from '@mui/icons-material/Info';
import {
  CancelButton,
  ArrowButton,
  hasValue,
  useTemp,
} from 'swipeclock-frontend';
import { useBootstrapAuth } from 'components/BootstrapAuthContext';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import Form from './Form';
import { useHistory } from 'react-router-dom';
import PoweredByFooter from '../shared/PoweredByFooter';
import { useWhiteLabelContext } from 'providers/WhiteLabelProvider';
import {
  useFinalizePunchResult,
  useCachedDataForIntelligentClocks,
  useClockConnectData,
} from '../../hooks/useWebclock';
import { getGeoLocation, readStoredGeoResult } from '../../utils/geoLocation';
import {
  usePromptsForm,
  usePrepareClockPromptsForPunchRequest,
} from '../../hooks/usePrompts';
import { useInactivityTimeout } from '../../hooks/useInactivityTimeout';
import {
  useIsMobileMode,
  useIsOfflineShellMode,
} from '../../hooks/useMobileMode';
import { useOfflinePunchQueue } from '../../hooks/useOfflinePunchQueue';
import {
  isAuthError,
  isBusinessError,
  isNetworkError,
  isTransportError,
} from '../../utils/isNetworkError';
import {
  applyOfflineRouteData,
  buildOfflineSaveFailureNavigation,
  buildOfflineSaveNavigation,
} from '../../utils/offlinePunchRouting';
import { awaitWithPunchWarning } from '../../utils/awaitWithPunchWarning';
import { PUNCH_TIMEOUT_WARNING_MESSAGE } from '../../constants/punchSubmitTimeoutConstants';
import InactivityWarningSnackbar from '../InactivityWarningSnackbar';

const navigateWithOfflineRouteData = (history, addToTemp, navigation) => {
  flushSync(() => {
    applyOfflineRouteData(addToTemp, navigation.routeData);
  });
  history.replace(navigation.route);
};

const INACTIVITY_COUNTDOWN_SECONDS = 15;

export default function MyComponent() {
  const [submitting, setSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const finishAbortRef = useRef(null);
  const { formBag, prompts } = usePromptsForm();
  const { state: whiteLabelState } = useWhiteLabelContext();
  const { isEnclosedMode, labelColor } = whiteLabelState || {};

  const canPunch = !formBag?.disabled;

  // Decide on how to route to errors. Do we pass history to useSubmitForm?
  // Or do we use a callback to handle the error?
  const { prepareClockPrompts } =
    usePrepareClockPromptsForPunchRequest(formBag);

  const { expectedPunches, punch, requestType, overridePunch, punchSessionId } =
    useCachedDataForIntelligentClocks();

  const { data: clockConnectData } = useClockConnectData();
  const { handleFinalizePunchResult } = useFinalizePunchResult(requestType);
  const { isMobileMode } = useIsMobileMode();
  const { isOfflineShellMode } = useIsOfflineShellMode();
  const { enqueuePunch, isOffline, reportApiUnreachable } =
    useOfflinePunchQueue();
  const { reportExpiredSession } = useBootstrapAuth();
  const { addToTemp } = useTemp('webClock');

  const history = useHistory();

  const buildOfflineEnqueuePayload = useCallback(
    (clockPrompts, geoResult) => ({
      source: 'prompts',
      punch,
      requestType,
      clockPrompts,
      expectedPunches,
      ...(hasValue(punchSessionId) && { punchSessionId }),
      ...(geoResult || {}),
      overridePunch: isOffline ? true : overridePunch,
    }),
    [
      punch,
      requestType,
      expectedPunches,
      punchSessionId,
      overridePunch,
      isOffline,
    ]
  );

  const handleCancel = () => {
    finishAbortRef.current?.abort();
    history.replace('/clock/web');
  };

  const handleFinish = async () => {
    if (isSubmittingRef.current) {
      return;
    }
    isSubmittingRef.current = true;
    const abortController = new AbortController();
    finishAbortRef.current = abortController;
    const { signal } = abortController;

    const clockPrompts = prepareClockPrompts();
    const data = {
      clockPrompts,
      expectedPunches,
    };

    const enqueueOfflinePromptPunch = async (priorResult) => {
      if (!hasValue(clockPrompts)) {
        navigateWithOfflineRouteData(
          history,
          addToTemp,
          buildOfflineSaveFailureNavigation({
            punch,
            message: 'Unable to save your punch offline. Please try again.',
          })
        );
        return;
      }

      try {
        const storedGeoResult = readStoredGeoResult(priorResult);
        const geoResult =
          storedGeoResult ||
          (await getGeoLocation(
            isMobileMode,
            clockConnectData?.geoLocationRequired
          ));
        if (signal.aborted) {
          return;
        }
        await enqueuePunch(buildOfflineEnqueuePayload(clockPrompts, geoResult));
        navigateWithOfflineRouteData(
          history,
          addToTemp,
          buildOfflineSaveNavigation({ punch, requestType })
        );
      } catch (saveError) {
        navigateWithOfflineRouteData(
          history,
          addToTemp,
          buildOfflineSaveFailureNavigation({
            punch,
            message: saveError?.message,
          })
        );
      }
    };

    try {
      setSubmitting(true);

      if (isOfflineShellMode && isOffline) {
        await enqueueOfflinePromptPunch();
        return;
      }

      const finalizePunch = handleFinalizePunchResult(punch, data, { signal });
      if (isOfflineShellMode) {
        await awaitWithPunchWarning(finalizePunch, () => {
          if (signal.aborted) {
            return;
          }

          navigateWithOfflineRouteData(history, addToTemp, {
            route: '/clock/web/pop-ups/punch/alert/warning',
            routeData: {
              punch,
              punchType: punch,
              requestType,
              warning: PUNCH_TIMEOUT_WARNING_MESSAGE,
            },
          });
        });
      } else {
        await finalizePunch;
      }
      if (signal.aborted) {
        return;
      }
      history.replace('/clock/web/pop-ups/punch/alert/success');
    } catch (error) {
      if (signal.aborted) {
        return;
      }
      if (isMobileMode && isAuthError(error)) {
        setSubmitting(false);
        reportExpiredSession();
        history.replace('/clock/web');
        return;
      }
      if (isOfflineShellMode && isTransportError(error)) {
        reportApiUnreachable();
      }
      if (
        isOfflineShellMode &&
        !error?.screenName &&
        !isBusinessError(error) &&
        (isOffline || isNetworkError(error))
      ) {
        await enqueueOfflinePromptPunch(error);
        return;
      }

      setSubmitting(false);
      console.error('Error preparing clock prompts:', error);
      history.replace('/clock/web/pop-ups/punch/alert/failure');
    } finally {
      isSubmittingRef.current = false;
      if (finishAbortRef.current === abortController) {
        finishAbortRef.current = null;
      }
    }
  };

  const { showInactivityCountdown, countdownTimer } = useInactivityTimeout({
    inactivity: 30,
    countdown: INACTIVITY_COUNTDOWN_SECONDS,
    onTimeout: canPunch ? handleFinish : handleCancel,
    enabled: !submitting,
  });

  return (
    <>
      <InactivityWarningSnackbar
        canPunch={canPunch}
        countdownDuration={INACTIVITY_COUNTDOWN_SECONDS}
        countdownTimer={countdownTimer}
        open={showInactivityCountdown}
      />
      <PromptContentCard
        id="prompt-content-card"
        isEnclosedMode={isEnclosedMode}
        customColor={labelColor}
      >
        <HeaderStack>
          <InfoIcon fontSize="large" color="primary" />
          <Typography variant="h4">More Information Needed</Typography>
        </HeaderStack>
        <Divider />
        <Form
          key={prompts.map((prompt) => prompt.id).join(',')}
          formBag={formBag}
          prompts={prompts}
        />
        <Footer>
          <FooterCancelButton
            id="sc-clock_prompts_cancel_punch_button"
            onClick={handleCancel}
          >
            Cancel Punch
          </FooterCancelButton>
          <FooterArrowButton
            id="sc-clock_prompts_finish_punch_button"
            disabled={!canPunch || submitting}
            loading={submitting}
            onClick={handleFinish}
          >
            Finish Punch
          </FooterArrowButton>
        </Footer>
      </PromptContentCard>
      <PoweredByFooter whiteLabelState={whiteLabelState} />
    </>
  );
}

// #########################
// ### Styled Components ###
// #########################

const PromptContentCard = styled(Card, {
  shouldForwardProp: (prop) =>
    prop !== 'isEnclosedMode' && prop !== 'customColor',
})(({ theme, customColor, isEnclosedMode }) => ({
  width: isEnclosedMode ? '100%' : '90vw',
  display: isEnclosedMode ? 'flex' : 'grid',
  flexDirection: isEnclosedMode ? 'column' : 'initial',
  gap: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  boxShadow: isEnclosedMode ? 'none' : theme.shadows[4],
  position: 'relative',
  padding: theme.spacing(4),
  // overflow: 'hidden',
  minHeight: isEnclosedMode ? '100%' : '33vh',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '4px',
    background: hasValue(customColor)
      ? customColor
      : theme.palette.primary.main,
  },
  [theme.breakpoints.down('mobile')]: {
    width: '100vw',
    minHeight: '100vh',
    placeItems: 'flex-start',
    // overflowY: 'auto',
  },
  [theme.breakpoints.up('sm')]: {
    width: isEnclosedMode ? '100%' : 512,
    margin: '0 auto',
  },
}));

const Footer = styled(Stack)(({ theme }) => ({
  flexDirection: 'column-reverse',
  justifyContent: 'center',
  alignItems: 'center',
  gap: theme.spacing(1),
  width: '100%',
  marginTop: 'auto',
  [theme.breakpoints.up('sm')]: {
    justifyContent: 'space-between',
    flexDirection: 'row',
    gap: theme.spacing(2),
  },
}));

const HeaderStack = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'flex-start',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const FooterCancelButton = styled((props) => <CancelButton {...props} />)(
  ({ theme }) => ({
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: 'auto',
    },
  })
);

const FooterArrowButton = styled((props) => <ArrowButton {...props} />)(
  ({ theme }) => ({
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: 'auto',
    },
  })
);
